import React, { type FunctionComponent, useMemo } from 'react'
import styled from '@emotion/styled'
import { Button, spacing, Text, colors } from '@retailer-platform/shared-components'
import { useDomainMessages } from 'domains/aggregate-store-performance/utils/domain/intl'
import { FullScreenModal } from 'domains/aggregate-store-performance/components/full-screen-modal/FullScreenModal'
import { StoreGroupsListBody } from 'legacy/sections/store-groups/components/list/components/StoreGroupsListBody'
import { useListStoreGroups } from 'legacy/common/graphql/store-groups/list.hooks'

const ActionButton = styled(Button)({
  height: spacing.X56,
  width: '190px',
  margin: 'spacing.X12 spacing.X16',
})

const Header = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyItems: 'flex-start',
  alignItems: 'center',
  width: '100%',
  padding: `${spacing.X8} 0 ${spacing.X8} ${spacing.X20}`,
  borderBottom: `1px solid ${colors.GRAYSCALE.X30}`,
})

const AddNewButton = styled(Button)({
  marginLeft: 'auto',
  marginRight: 0,
})

interface EditStoreGroupsModalProps {
  isOpen: boolean
  onClose: () => void
  onEditClicked: (storeGroupId: string) => void
  onAddNewClicked: () => void
}

export const EditStoreGroupsModal: FunctionComponent<EditStoreGroupsModalProps> = ({
  isOpen,
  onClose,
  onEditClicked,
  onAddNewClicked,
}) => {
  const messages = useDomainMessages({
    modalTitle: 'aggregateStorePerformanceDomain.editStoreGroupsModal.title',
    header: 'aggregateStorePerformanceDomain.editStoreGroupsModal.header',
    addNew: 'aggregateStorePerformanceDomain.editStoreGroupsModal.addNew',
    cancel: 'aggregateStorePerformanceDomain.editStoreGroupsModal.cancel',
  })

  const { storeGroups, loading, error } = useListStoreGroups()

  const body = useMemo(
    () => (
      <>
        <Header>
          <Text size="large" weight="semibold">
            {messages.header}
          </Text>
          <AddNewButton onClick={onAddNewClicked}>{messages.addNew}</AddNewButton>
        </Header>
        <StoreGroupsListBody
          storeGroups={storeGroups}
          loading={loading}
          error={error}
          onEditClicked={onEditClicked}
        />
      </>
    ),
    [messages.header, messages.addNew, storeGroups, loading, error, onAddNewClicked, onEditClicked]
  )

  const footer = useMemo(
    () => (
      <>
        <ActionButton variant="secondary" onClick={onClose}>
          {messages.cancel}
        </ActionButton>
      </>
    ),
    [messages.cancel, onClose]
  )

  return (
    <FullScreenModal
      onClose={onClose}
      isOpen={isOpen}
      title={messages.modalTitle}
      body={body}
      footer={footer}
    />
  )
}
